import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { I18n } from 'react-redux-i18n';
import { TimeInput } from './formatTimeElapsed';

dayjs.extend(utc);

const unitLabel = (unit: 'year' | 'month' | 'day', value: number): string =>
  I18n.t(`general.time-ago.units.${unit}_short`, { count: value });

/**
 * Formats the age of a past UTC date as compact whole years, months and days,
 * omitting zero units (e.g. "7y 8m 20d", "3m 9d", "2y"). A same-day age is "0d".
 * Returns the fallback for null, invalid, or future dates.
 */
export const formatYearsMonthsDays = (
  utcDate: TimeInput,
  fallback: string | null = null
): string | null => {
  if (!utcDate) {
    return fallback;
  }

  const start = dayjs.utc(utcDate).local();
  const end = dayjs();

  if (!start.isValid() || end.isBefore(start)) {
    return fallback;
  }

  const years = end.diff(start, 'year');
  const afterYears = start.add(years, 'years');
  const months = end.diff(afterYears, 'month');
  const days = end.diff(afterYears.add(months, 'months'), 'day');

  const parts: string[] = [];
  if (years > 0) {
    parts.push(unitLabel('year', years));
  }
  if (months > 0) {
    parts.push(unitLabel('month', months));
  }
  if (days > 0 || parts.length === 0) {
    parts.push(unitLabel('day', days));
  }
  return parts.join(' ');
};

export default formatYearsMonthsDays;
